import React, { ReactNode, PureComponent, ComponentType } from 'react';
import { T } from 'app/components/localization';
import {
  MULTI_SELECT_POSITIVE,
  MULTI_SELECT_POSITIVE_HIGHLIGHT,
  MULTI_SELECT_NEGATIVE,
  MULTI_SELECT_NEGATIVE_HIGHLIGHT,
  ALERT_TEXT_COLOR,
  GREY_D,
} from 'app/constants/Colors';
import { BORDER_RADIUS_A } from 'app/constants/Styles';
import Flex, { Alignment } from 'app/components/layout/Flex';
import FlexChild from 'app/components/layout/FlexChild';
import Selectable from 'app/components/common/Selectable';
import { IEnumOption } from 'app/components/forms/MultiSelect/Value';
import { createStyles } from 'app/utils/css';
import BaseButton from 'app/components/common/BaseButton';
import { CallbackRef } from 'app/utils/react';

export interface IOptionProps<ValueType> {
  children?: ReactNode;
  index: number;
  itemRef: (index: number) => CallbackRef;
  option: ValueType;
  onSelect: (value: ValueType) => unknown;
}

export type OptionComponent<ValueType> = ComponentType<IOptionProps<ValueType>>;

/**
 * Helper for building optionsComponent for MultiSelect.
 */
export class Option<ValueType> extends PureComponent<IOptionProps<ValueType>> {
  public render() {
    const { children, itemRef, index } = this.props;
    return (
      <Selectable onClick={this.onSelect} getRef={itemRef(index)}>
        {children}
      </Selectable>
    );
  }

  private onSelect = (e: React.MouseEvent) => {
    e.preventDefault();
    return this.props.onSelect(this.props.option);
  };
}

export const TextOption = ({ option, ...props }: IOptionProps<string>) => (
  <Option option={option} {...props}>{option}</Option>);

interface IValue<IdType extends string> {
  id: IdType;
  value: boolean | undefined;
}

/**
 * Helper for building optionsComponent for MultiSelect that have inclusive and
 * exclusive selectors.
 *
 * The option is an object containing the option id and a boolean or undefined
 * value. Undefined is used in the event that the option is unchecked.
 */
export class BooleanOption<IdType extends string> extends PureComponent<IOptionProps<IValue<IdType>>> {
  public render() {
    const { children, itemRef, index, option } = this.props;
    const { value } = option;
    return (
      <Styles>{(css) =>
        <Flex align={Alignment.Center}>
          <FlexChild grow={0} shrink={0}>
            <div style={{ fontSize: '0.8em', padding: '5px 10px' }}>
              <BaseButton
                className={css('button', { positive: true, value })}
                onClick={this.onPositive}
                getRef={itemRef(index * 2)}
              >
                +
              </BaseButton>
              <BaseButton
                className={css('button', { positive: false, value })}
                onClick={this.onNegative}
                getRef={itemRef((index * 2) + 1)}
              >
                &minus;
              </BaseButton>
            </div>
          </FlexChild>
          <Flex grow={1}>
            {children}
          </Flex>
        </Flex>}
      </Styles>
    );
  }

  private onSelect = (value: boolean) =>
    this.props.onSelect({
      id: this.props.option.id,
      value: this.props.option.value !== value ? value : undefined,
    });

  private onPositive = () => this.onSelect(true);
  private onNegative = () => this.onSelect(false);
}

const Styles = createStyles({
  button: ({ positive, value }) => [
    {
      border: `1px solid ${ALERT_TEXT_COLOR}`,
      borderCollapse: 'collapse',
      cursor: 'pointer',
      display: 'inline-block',
      padding: '0.2em 0.8em',
      backgroundColor: (
        positive ? (
          value ? MULTI_SELECT_POSITIVE_HIGHLIGHT : MULTI_SELECT_POSITIVE
        ) : (
          // value may be undefined, so we have to explicitly match for false
          value === false ? MULTI_SELECT_NEGATIVE_HIGHLIGHT : MULTI_SELECT_NEGATIVE
        )
      ),
    },

    positive ? {
      borderRight: 'none',
      borderRadius: `${BORDER_RADIUS_A} 0 0 ${BORDER_RADIUS_A}`,
    } : {
      borderRadius: `0 ${BORDER_RADIUS_A} ${BORDER_RADIUS_A} 0`,
    },
  ],
});

export const EnumOption = ({ option, ...props }: IOptionProps<IEnumOption>) => (
  <T>{t => {
    const name = option.getName(t);
    const description = typeof option.getDescription === 'function' && option.getDescription(t);
    return (
      <Option option={option} {...props}>
        <div>{name}</div>
        {description && <div style={{ color: GREY_D, fontSize: '0.8em' }}>{description}</div>}
      </Option>
    );
  }}</T>
);
